import { FacilityListParams, FacilitySort } from '@gyym/sdk'
import { useRouter } from 'next/router'
import React from 'react'

import useGeolocation, { Location } from '@/hooks/useGeolocation'
import { searchHistory, searchQueryConverter } from '@/modules/FacilitySearch'
import { usePrefectures } from '@/modules/Prefectures'

type SearchOptions = { isMapView?: boolean }

export interface SearchContextValue {
  isMapView: boolean
  isFiltered: boolean
  searchParams: FacilityListParams
  /**
   * searchParams + 現在地 (distance ソート時のみ)。
   * facilities.list の SWR キーはこれで統一し、コンポーネント間で
   * キーがずれて二重フェッチになるのを防ぐ
   */
  locatedSearchParams: FacilityListParams
  currentLocation: Location
  isLocationResolved: boolean
  getLocation: () => Promise<Location>
  search: (params: FacilityListParams, options?: SearchOptions) => void
}

const SearchContext = React.createContext<SearchContextValue>({
  isMapView: false,
  isFiltered: false,
  searchParams: {},
  locatedSearchParams: {},
  currentLocation: { lat: 0, lng: 0 },
  isLocationResolved: false,
  getLocation: () => {
    throw new Error('Provider uninitialized')
  },
  search: () => {
    throw new Error('Provider uninitialized')
  },
})

export interface SearchProviderProps {
  // locatedSearchParams / currentLocation / getLocation は Provider 内部で導出する
  value: Pick<SearchContextValue, 'isMapView' | 'searchParams'>
  children: React.ReactNode
}

export const SearchProvider = ({
  value: { isMapView = false, searchParams = {} },
  children,
}: SearchProviderProps) => {
  const router = useRouter()
  const { data: prefectures } = usePrefectures()

  const [isMapViewState, setIsMapViewState] = React.useState(isMapView)

  const [searchParamsState, setSearchParamsState] =
    React.useState<FacilityListParams>(searchParams)

  const { currentLocation, getLocation, isLocationResolved } = useGeolocation({
    // Prompt for location permission if sortBy is distance
    skipInitialPrompt: searchParamsState.sortBy !== FacilitySort.DISTANCE,
  })

  // 位置情報は URL クエリに載らずブラウザでしか取れないため、
  // distance ソート時のみここで検索パラメータに合成する
  const locatedSearchParams = React.useMemo(
    () =>
      searchParamsState.sortBy === FacilitySort.DISTANCE && isLocationResolved
        ? {
            ...searchParamsState,
            latitude: currentLocation.lat,
            longitude: currentLocation.lng,
          }
        : searchParamsState,
    [searchParamsState, currentLocation, isLocationResolved],
  )

  const {
    prefectureIds = [],
    activityIds = [],
    areaIds = [],
    dates = [],
    exerciseLevels = [],
    sexLimitations = [],
    facilityName,
    priceFrom,
    priceTo,
    startTimeFrom,
    startTimeTo,
    cityCode,
  } = searchParamsState

  const isFiltered =
    prefectureIds.length > 0 ||
    activityIds.length > 0 ||
    areaIds.length > 0 ||
    dates.length > 0 ||
    exerciseLevels.length > 0 ||
    sexLimitations.length > 0 ||
    !!facilityName ||
    !!priceFrom ||
    !!priceTo ||
    !!startTimeFrom ||
    !!startTimeTo ||
    !!cityCode

  const search = React.useCallback(
    (
      params: Partial<FacilityListParams>,
      { isMapView }: SearchOptions = {},
    ) => {
      if (!prefectures) {
        console.log('Prefectures not loaded yet')
        return
      }

      const query = {
        ...searchParamsState,
        ...params,
      }

      if (isMapView !== undefined) {
        setIsMapViewState(isMapView)
      }

      setSearchParamsState(query)

      // Set search history
      searchHistory.set(query)

      const { href, as } = searchQueryConverter.toLink(query, prefectures, {
        isMapView: isMapView === undefined ? isMapViewState : isMapView,
      })

      router.push(href, as, {
        // Keep it shallow since facility search pages renders the same view component
        shallow: true,
      })
    },
    [router, prefectures, searchParamsState, isMapViewState],
  )

  // Update search params when router query changes
  React.useEffect(() => {
    setSearchParamsState(searchParams)
  }, [searchParams])

  return (
    <SearchContext.Provider
      value={{
        isFiltered,
        isMapView: isMapViewState,
        searchParams: searchParamsState,
        locatedSearchParams,
        currentLocation,
        isLocationResolved,
        getLocation,
        search,
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

export const useSearch = () => {
  return React.useContext(SearchContext)
}
